import type { Row, RowData } from '@tanstack/react-table';
import * as React from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { CursorPagination } from '../data-table/cursor-pagination';

import { DataTableBulkActions } from './data-table-bulk-actions';
import { DataTableColumnHeader } from './data-table-column-header';
import { DataTableFilterBar } from './data-table-filter-bar';
import { DataTablePagination } from './data-table-pagination';
import { DataTableRowActions } from './data-table-row-actions';
import {
  DataTableEmptyState,
  DataTableErrorState,
  DataTableLoadingState,
} from './data-table-states';
import type { DataTableFeatures } from './features';
import { pillFilterDef, pillViews } from './pill-views';
import type { BulkAction, DataTableView, RowAction } from './types';
import type { DataTableInstance } from './use-data-table';
import { useDataTableViews } from './use-data-table-views';

export interface DataTableProps<TData extends RowData> {
  /** Built with `useDataTable`: that hook holds the state, this component renders it. */
  table: DataTableInstance<TData>;

  /* Toolbar ---------------------------------------------------------------- */
  /** Show the search and filter bar. Default true. */
  toolbar?: boolean;
  /** `false` turns off free-text search (the bar then only adds filters). */
  search?:
    | false
    | {
        placeholder?: string;
        label?: string;
        /** Pause after typing before the text searches. Default 300 ms. */
        debounceMs?: number;
        /** What the search covers and its typed syntax, behind a "?" in the field. */
        help?: string;
      };
  /**
   * The filter behind the permanent pill at the start of the bar: "All" plus one choice per value
   * of an enum or yes/no filter, which also shows as a chip in the field. Default: the `status`
   * filter when the table has one and no `views` of its own. `false` for no pill.
   */
  pillFilter?: string | false;
  /** More choices in the pill, after the filter's; alone, the pill's whole list. */
  views?: readonly DataTableView[];
  /** Saved views, controlled (store them on your server to share them). Default: this browser, under the table's `tableId`. */
  savedViews?: readonly DataTableView[];
  onSavedViewsChange?: (views: DataTableView[]) => void;
  /** Show the sort and columns panel. Default true. */
  displayOptions?: boolean;
  /** Extra controls at the end of the toolbar: Export, a primary action. */
  toolbarActions?: React.ReactNode;

  /* Rows ------------------------------------------------------------------- */
  /** Checkbox column, Shift-click ranges, bulk actions. Default: when `bulkActions` are given. */
  selectable?: boolean;
  bulkActions?: ReadonlyArray<BulkAction<TData>>;
  /**
   * Offer "Select all N" across pages. Default true; never offered without a
   * total (cursor mode without `total`), where bulk actions run on the
   * selected loaded rows. Cursor mode with `total` offers it when an action
   * `supportsAllMatching`.
   */
  selectAllMatching?: boolean;
  /**
   * The most rows "Select all N" may cover (the bulk endpoint's limit, 5,000
   * for products). Above it the bar shows "Narrow the filter" instead.
   */
  allMatchingMax?: number;
  /** The "..." menu in the last column. */
  rowActions?: ReadonlyArray<RowAction<TData>>;
  /** Row click (open the record). While rows are selected, a click toggles selection instead. */
  onRowClick?: (row: TData) => void;
  /** Accessible name of a row, for "Select {name}" and "Actions for {name}". Default: its id. */
  getRowLabel?: (row: TData) => string;
  /** Translated nouns for copy: "12 products selected". Default `common:indexTable.item`. */
  resourceName?: { singular: string; plural: string };

  /* States ----------------------------------------------------------------- */
  /** First load: skeleton rows. */
  loading?: boolean;
  /** Background refetch: a thin progress bar; rows stay readable. */
  fetching?: boolean;
  /** Query error; rendered with `ErrorState`, which shows the request id. */
  error?: unknown;
  onRetry?: () => void;
  /** Shown when nothing matches (filtered) or there is no data at all. */
  emptyState?: {
    title?: string;
    description?: string;
    action?: React.ReactNode;
  };

  /* Layout ----------------------------------------------------------------- */
  /**
   * Show the pagination footer. Default: when the table paginates. Cursor
   * mode shows first / previous / next and the page size instead of page numbers.
   */
  pagination?: boolean;
  /** Cursor mode: the footer says "20 of 63 products" instead of "Showing 20". Needs a total. */
  footerTotal?: boolean;
  /** Page mode only; cursor mode offers the backend's limits. */
  pageSizeOptions?: readonly number[];
  /**
   * The toolbar sticks to the viewport this far from the top (a fixed site
   * header's height). Default 0.
   */
  stickyOffset?: number | string;
  /** Caps the table's height; rows then scroll under a sticky header row. */
  maxHeight?: number | string;
  /**
   * Pins the row-actions column (or, without row actions, the last data column) to the right
   * edge, so a wide table scrolls between the pinned first and last columns.
   */
  stickyLastColumn?: boolean;
  className?: string;
  /**
   * `data-tour` values for guided tours: the whole table, its search box, its filter bar, the
   * row-actions column header.
   */
  tour?: { table?: string; search?: string; filters?: string; rowActions?: string };
}

const SELECT_CELL = 'sticky left-0 z-[2] w-10 min-w-10 max-w-10 px-3 text-center';
const HEAD_CELL =
  'h-10 border-b bg-table-header px-3 text-left align-middle text-sm font-medium whitespace-nowrap text-muted-foreground';
const BODY_CELL =
  'border-b bg-background px-3 py-2.5 align-middle whitespace-nowrap group-last/row:border-b-0 group-hover/row:bg-[color-mix(in_oklab,var(--muted)_60%,var(--background))] group-data-[selected]/row:bg-[color-mix(in_oklab,var(--accent)_70%,var(--background))]';
/** The first data column sticks next to the checkbox and casts an edge once scrolled. */
const STICKY_FIRST = 'sticky z-[1] in-data-[scrolled]:shadow-[inset_-1px_0_0_var(--border)]';
/** The pinned last column casts its edge while columns are still hidden under it. */
const STICKY_LAST =
  'sticky right-0 z-[1] in-data-[more-right]:shadow-[inset_1px_0_0_var(--border)]';

const INTERACTIVE =
  'a, button, input, select, textarea, label, [role="checkbox"], [role="menuitem"], [data-no-row-click]';

/**
 * A Shopify-style index table: the search and filter bar (views, chips,
 * typed qualifiers, Save), sortable and filterable headers, sticky checkbox
 * and first columns, Shift-click selection with bulk actions and "Select all
 * N", row actions, and pagination (page numbers, or a cursor footer for
 * cursor-paginated endpoints). All state comes from `useDataTable`.
 */
function DataTable<TData extends RowData>({
  table,
  toolbar = true,
  search,
  pillFilter,
  views: ownViews,
  savedViews,
  onSavedViewsChange,
  displayOptions = true,
  toolbarActions,
  selectable: selectableProp,
  bulkActions = [],
  selectAllMatching = true,
  allMatchingMax,
  rowActions = [],
  onRowClick,
  getRowLabel,
  resourceName: resourceNameProp,
  loading = false,
  fetching = false,
  error,
  onRetry,
  emptyState,
  pagination = table.paginate,
  footerTotal = false,
  pageSizeOptions,
  stickyOffset = 0,
  maxHeight,
  stickyLastColumn = false,
  className,
  tour,
}: DataTableProps<TData>) {
  const { t } = useTranslation('common');
  const resourceName = resourceNameProp ?? {
    singular: t('indexTable.item_one'),
    plural: t('indexTable.item_other'),
  };
  const { core, selection, rows } = table;
  const selectable = selectableProp ?? bulkActions.length > 0;
  const hasRowActions = rowActions.length > 0;
  const visibleColumns = core.getVisibleLeafColumns();
  const firstColumnId = visibleColumns[0]?.id;
  const lastColumnId =
    stickyLastColumn && !hasRowActions && visibleColumns.length > 1
      ? visibleColumns.at(-1)?.id
      : undefined;
  const actionsSticky = stickyLastColumn && hasRowActions;
  const columnCount = visibleColumns.length + (selectable ? 1 : 0) + (hasRowActions ? 1 : 0);
  const selecting = selectable && selection.hasSelection;

  const pillDef = pillFilterDef(table.filters, pillFilter, ownViews !== undefined);
  const builtInViews = pillDef
    ? [
        ...pillViews(pillDef, {
          all: t('indexTable.views.all'),
          yes: t('indexTable.boolean.yes'),
          no: t('indexTable.boolean.no'),
        }),
        ...(ownViews ?? []),
      ]
    : ownViews;

  const views = useDataTableViews({
    list: table.list,
    views: builtInViews,
    savedViews,
    onSavedViewsChange,
    storageKey: table.tableId ? `nucleus:index-table:${table.tableId}:views` : undefined,
  });

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = React.useState(false);
  const [moreRight, setMoreRight] = React.useState(false);
  const measureScroll = React.useCallback((element: HTMLDivElement) => {
    setScrolled(element.scrollLeft > 0);
    setMoreRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 1);
  }, []);
  // The visible width, so the bulk-action bar stays on screen (and wraps)
  // while a wide table scrolls sideways under it.
  React.useEffect(() => {
    const element = scrollRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      element.style.setProperty('--fujin-data-table-width', `${entry.contentRect.width}px`);
      measureScroll(element);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [measureScroll]);

  const labelOf = (row: Row<DataTableFeatures, TData>) => getRowLabel?.(row.original) ?? row.id;

  const onRowClickCapture = (
    row: Row<DataTableFeatures, TData>,
    event: React.MouseEvent<HTMLTableRowElement>,
  ) => {
    if ((event.target as HTMLElement).closest(INTERACTIVE)) return;
    // Text being selected is not a click.
    if (window.getSelection()?.toString()) return;
    if (selecting) {
      selection.toggleRow(row.id, !selection.isRowSelected(row.id), {
        range: event.shiftKey,
      });
      return;
    }
    onRowClick?.(row.original);
  };

  // A view can match nothing with no changes on top: clearing then leaves the view.
  const clearQuery = () => {
    if (views.enabled && views.isModified) views.revert();
    else if (views.enabled && views.views[0]) views.select(views.views[0]);
    else table.list.update({ q: undefined, filters: {} });
  };

  // Without a total (cursor mode), "20+ found" while more pages follow.
  const foundCount = table.rowCount ?? rows.length;
  const foundText = t(
    (table.rowCount === undefined && table.cursorPaging?.hasMore) || table.rowCountCapped
      ? 'indexTable.foundMore'
      : 'indexTable.found',
    {
      count: foundCount,
      resource: foundCount === 1 ? resourceName.singular : resourceName.plural,
    },
  );

  const style = {
    '--fujin-data-table-offset':
      typeof stickyOffset === 'number' ? `${stickyOffset}px` : stickyOffset,
  } as React.CSSProperties;

  return (
    <div
      data-slot="data-table"
      data-tour={tour?.table}
      className={cn('bg-background rounded-lg border', className)}
      style={style}
    >
      {toolbar ? (
        <DataTableFilterBar
          table={table}
          views={views}
          search={search}
          displayOptions={displayOptions}
          actions={toolbarActions}
          tour={tour}
          className="bg-background sticky top-[var(--fujin-data-table-offset)] z-20 rounded-t-lg border-b"
        />
      ) : null}
      {/* Announces the result count after a search or filter, as Primer asks. */}
      <p role="status" className="sr-only">
        {table.isFiltered && !loading ? foundText : ''}
      </p>
      <div className="relative">
        {fetching && !loading ? (
          <div
            role="progressbar"
            aria-label={t('indexTable.loading', { resource: resourceName.plural })}
            className="bg-primary absolute inset-x-0 top-0 z-30 h-0.5 animate-pulse"
          />
        ) : null}
        <div
          ref={scrollRef}
          data-slot="data-table-scroll"
          data-scrolled={scrolled || undefined}
          data-more-right={moreRight || undefined}
          onScroll={(event) => measureScroll(event.currentTarget)}
          // Without a toolbar the shaded header row is the top edge, so it follows the corners.
          className={cn('relative w-full overflow-auto', !toolbar && 'rounded-[inherit]')}
          style={maxHeight !== undefined ? { maxHeight } : undefined}
        >
          <table
            data-slot="data-table-table"
            aria-busy={loading || fetching || undefined}
            // -1: the total is unknown (ARIA's value for it).
            aria-rowcount={
              loading ? undefined : table.rowCount === undefined ? -1 : table.rowCount + 1
            }
            className="w-full border-separate border-spacing-0 text-sm"
          >
            <thead className={cn(maxHeight !== undefined && 'sticky top-0 z-10')}>
              {selecting ? (
                <DataTableBulkActions
                  table={table}
                  actions={bulkActions}
                  selectAllMatching={selectAllMatching}
                  allMatchingMax={allMatchingMax}
                  resourceName={resourceName}
                  columnCount={columnCount}
                  cellClassName={HEAD_CELL}
                  selectCellClassName={cn(HEAD_CELL, SELECT_CELL)}
                />
              ) : (
                core.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {selectable ? (
                      <th scope="col" className={cn(HEAD_CELL, SELECT_CELL)}>
                        <Checkbox
                          checked={
                            selection.isAllPageRowsSelected ||
                            (selection.isSomePageRowsSelected && 'indeterminate')
                          }
                          disabled={rows.length === 0}
                          onCheckedChange={(checked) => selection.togglePage(checked === true)}
                          aria-label={t('indexTable.selection.selectPage', {
                            resource: resourceName.plural,
                          })}
                          className="relative after:absolute after:-inset-1"
                        />
                      </th>
                    ) : null}
                    {headerGroup.headers.map((header) => {
                      const { column } = header;
                      const sortField = table.sortFieldOf(column);
                      const sorted = column.getIsSorted();
                      const align = column.columnDef.meta?.align;
                      return (
                        <th
                          key={header.id}
                          scope="col"
                          colSpan={header.colSpan}
                          aria-sort={
                            sortField === undefined
                              ? undefined
                              : sorted === 'asc'
                                ? 'ascending'
                                : sorted === 'desc'
                                  ? 'descending'
                                  : 'none'
                          }
                          data-column={column.id}
                          data-tour={column.columnDef.meta?.tour}
                          className={cn(
                            HEAD_CELL,
                            column.id === firstColumnId &&
                              cn(STICKY_FIRST, selectable ? 'left-10' : 'left-0'),
                            column.id === lastColumnId && STICKY_LAST,
                            align === 'end' && 'text-right',
                            align === 'center' && 'text-center',
                          )}
                        >
                          <DataTableColumnHeader table={table} header={header} />
                        </th>
                      );
                    })}
                    {hasRowActions ? (
                      <th
                        scope="col"
                        className={cn(HEAD_CELL, 'w-12', actionsSticky && STICKY_LAST)}
                        data-tour={tour?.rowActions}
                      >
                        <span className="sr-only">{t('indexTable.actions')}</span>
                      </th>
                    ) : null}
                  </tr>
                ))
              )}
            </thead>
            <tbody>
              {loading ? (
                <DataTableLoadingState
                  colSpan={columnCount}
                  label={t('indexTable.loading', { resource: resourceName.plural })}
                />
              ) : error !== undefined && error !== null ? (
                <DataTableErrorState colSpan={columnCount} error={error} onRetry={onRetry} />
              ) : rows.length === 0 ? (
                <DataTableEmptyState
                  colSpan={columnCount}
                  filtered={table.isFiltered}
                  title={
                    emptyState?.title ??
                    (table.isFiltered
                      ? t('indexTable.empty.filteredTitle', { resource: resourceName.plural })
                      : t('indexTable.empty.title', { resource: resourceName.plural }))
                  }
                  description={
                    emptyState?.description ??
                    (table.isFiltered ? t('indexTable.empty.filteredDescription') : undefined)
                  }
                  action={
                    emptyState?.action ??
                    (table.isFiltered ? (
                      <Button variant="outline" size="sm" onClick={clearQuery}>
                        {t('indexTable.filters.clear')}
                      </Button>
                    ) : undefined)
                  }
                />
              ) : (
                rows.map((row) => {
                  const selected = selectable && selection.isRowSelected(row.id);
                  const label = labelOf(row);
                  return (
                    <tr
                      key={row.id}
                      data-row-id={row.id}
                      data-selected={selected || undefined}
                      aria-selected={selectable ? selected : undefined}
                      onClick={(event) => onRowClickCapture(row, event)}
                      className={cn(
                        'group/row',
                        (onRowClick !== undefined || selecting) && 'cursor-pointer',
                      )}
                    >
                      {selectable ? (
                        <td className={cn(BODY_CELL, SELECT_CELL)}>
                          <Checkbox
                            checked={selected}
                            // Radix's onCheckedChange carries no event; Shift-click ranges need one.
                            onClick={(event) =>
                              selection.toggleRow(row.id, !selected, { range: event.shiftKey })
                            }
                            aria-label={t('indexTable.selection.selectRow', { label })}
                            className="relative after:absolute after:-inset-1"
                          />
                        </td>
                      ) : null}
                      {row.getVisibleCells().map((cell) => {
                        const meta = cell.column.columnDef.meta;
                        return (
                          <td
                            key={cell.id}
                            data-column={cell.column.id}
                            className={cn(
                              BODY_CELL,
                              cell.column.id === firstColumnId &&
                                cn(STICKY_FIRST, selectable ? 'left-10' : 'left-0'),
                              cell.column.id === lastColumnId && STICKY_LAST,
                              meta?.align === 'end' && 'text-right tabular-nums',
                              meta?.align === 'center' && 'text-center',
                              meta?.cellClassName,
                            )}
                          >
                            <core.FlexRender cell={cell} />
                          </td>
                        );
                      })}
                      {hasRowActions ? (
                        <td
                          className={cn(
                            BODY_CELL,
                            'w-12 px-2 text-right',
                            actionsSticky && STICKY_LAST,
                          )}
                        >
                          <DataTableRowActions
                            row={row.original}
                            actions={rowActions}
                            label={label}
                          />
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {pagination && table.cursorPaging ? (
        <nav
          aria-label={t('indexTable.pagination.label')}
          data-slot="data-table-pagination"
          className="border-t px-2"
        >
          <CursorPagination
            pager={table.cursorPaging.pager}
            nextCursor={table.cursorPaging.nextCursor}
            limit={table.cursorPaging.limit}
            onLimitChange={table.cursorPaging.setLimit}
            isFetching={fetching}
            count={loading ? undefined : rows.length}
            summary={
              footerTotal && !loading && table.rowCount !== undefined
                ? t(
                    table.rowCountCapped
                      ? 'indexTable.pagination.ofTotalCapped'
                      : 'indexTable.pagination.ofTotal',
                    {
                      shown: rows.length,
                      total: table.rowCount,
                      resource: table.rowCount === 1 ? resourceName.singular : resourceName.plural,
                    },
                  )
                : undefined
            }
          />
        </nav>
      ) : pagination ? (
        <DataTablePagination table={table} pageSizeOptions={pageSizeOptions} />
      ) : null}
    </div>
  );
}

export { DataTable };
